import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  DentalCaseData,
  DentalCaseJaws,
  JawType,
  ArchDefinition,
  ViewMode,
  PrescriptionItem,
  RestorationPlan,
} from "../types/dental";

interface CaseSessionState {
  currentCase: DentalCaseData | null;
  editableJaws: DentalCaseJaws | null;
  editablePrescriptionItems: PrescriptionItem[];
  editableRestorationPlans: RestorationPlan[];
  selectedCaseId: string | null;
  activeJaw: JawType;
  viewMode: ViewMode;
  isDirty: boolean;
  isSaving: boolean;
  saveError: string | null;
  selectedPrescriptionItemId: string | null;
}

interface CaseSessionContextValue extends CaseSessionState {
  setSelectedCaseId: (caseId: string | null) => void;
  setCaseData: (data: DentalCaseData) => void;
  setActiveJaw: (jaw: JawType) => void;
  setViewMode: (mode: ViewMode) => void;
  setSelectedPrescriptionItemId: (itemId: string | null) => void;
  updateActiveJawArch: <K extends keyof ArchDefinition>(
    key: K,
    value: ArchDefinition[K]
  ) => void;
  addRestorationPlan: (plan: RestorationPlan) => void;
  updateRestorationPlan: (
    planId: string,
    updates: Partial<RestorationPlan>
  ) => void;
  beginSave: () => void;
  completeSaveSuccess: () => void;
  completeSaveFailure: (message: string) => void;
  markClean: () => void;
  clearCase: () => void;
}

const CaseSessionContext = createContext<CaseSessionContextValue | undefined>(
  undefined
);

export function CaseSessionProvider({ children }: { children: ReactNode }) {
  const [currentCase, setCurrentCase] = useState<DentalCaseData | null>(null);
  const [editableJaws, setEditableJaws] = useState<DentalCaseJaws | null>(null);
  const [editablePrescriptionItems, setEditablePrescriptionItems] = useState<
    PrescriptionItem[]
  >([]);
  const [editableRestorationPlans, setEditableRestorationPlans] = useState<
    RestorationPlan[]
  >([]);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [activeJaw, setActiveJaw] = useState<JawType>("upper");
  const [viewMode, setViewMode] = useState<ViewMode>("upper");
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedPrescriptionItemId, setSelectedPrescriptionItemId] = useState<string | null>(null);

  function setCaseData(data: DentalCaseData): void {
    setCurrentCase(data);
    setEditableJaws(data.jaws);
    setEditablePrescriptionItems(data.prescriptionItems);
    setEditableRestorationPlans(data.restorationPlans);
    setSelectedCaseId(data.summary.caseId);
    setActiveJaw("upper");
    setViewMode("upper");
    setIsDirty(false);
    setIsSaving(false);
    setSaveError(null);

    const firstItem = data.prescriptionItems[0];
    setSelectedPrescriptionItemId(firstItem ? firstItem.itemId : null);
  }

  function updateActiveJawArch<K extends keyof ArchDefinition>(
    key: K,
    value: ArchDefinition[K]
  ): void {
    setEditableJaws((prev) => {
      if (!prev) {
        return prev;
      }

      const currentJaw = prev[activeJaw];
      if (currentJaw[key] === value) {
        return prev;
      }

      const nextJaw = {
        ...currentJaw,
        [key]: value,
      };

      setIsDirty(true);
      setSaveError(null);

      return {
        ...prev,
        [activeJaw]: nextJaw,
      };
    });
  }

  function addRestorationPlan(plan: RestorationPlan): void {
    setEditableRestorationPlans((prev) => [plan, ...prev]);
    setIsDirty(true);
    setSaveError(null);
  }

  function updateRestorationPlan(
    planId: string,
    updates: Partial<RestorationPlan>
  ): void {
    setEditableRestorationPlans((prev) =>
      prev.map((plan) =>
        plan.planId === planId
          ? {
              ...plan,
              ...updates,
            }
          : plan
      )
    );

    setIsDirty(true);
    setSaveError(null);
  }

  function beginSave(): void {
    setIsSaving(true);
    setSaveError(null);
  }

  function completeSaveSuccess(): void {
    setIsSaving(false);
    setIsDirty(false);
    setSaveError(null);

    setCurrentCase((prev) => {
      if (!prev || !editableJaws) {
        return prev;
      }

      return {
        ...prev,
        jaws: editableJaws,
        prescriptionItems: editablePrescriptionItems,
        restorationPlans: editableRestorationPlans,
      };
    });
  }

  function completeSaveFailure(message: string): void {
    setIsSaving(false);
    setSaveError(message);
  }

  function markClean(): void {
    setIsDirty(false);
    setSaveError(null);
  }

  function clearCase(): void {
    setCurrentCase(null);
    setEditableJaws(null);
    setEditablePrescriptionItems([]);
    setEditableRestorationPlans([]);
    setSelectedCaseId(null);
    setActiveJaw("upper");
    setViewMode("upper");
    setIsDirty(false);
    setIsSaving(false);
    setSaveError(null);
    setSelectedPrescriptionItemId(null);
  }

  const value = useMemo<CaseSessionContextValue>(
    () => ({
      currentCase,
      editableJaws,
      editablePrescriptionItems,
      editableRestorationPlans,
      selectedCaseId,
      activeJaw,
      viewMode,
      isDirty,
      isSaving,
      saveError,
      selectedPrescriptionItemId,
      setSelectedCaseId,
      setCaseData,
      setActiveJaw,
      setViewMode,
      setSelectedPrescriptionItemId,
      updateActiveJawArch,
      addRestorationPlan,
      updateRestorationPlan,
      beginSave,
      completeSaveSuccess,
      completeSaveFailure,
      markClean,
      clearCase,
    }),
    [
      currentCase,
      editableJaws,
      editablePrescriptionItems,
      editableRestorationPlans,
      selectedCaseId,
      activeJaw,
      viewMode,
      isDirty,
      isSaving,
      saveError,
      selectedPrescriptionItemId,
    ]
  );

  return (
    <CaseSessionContext.Provider value={value}>
      {children}
    </CaseSessionContext.Provider>
  );
}

export function useCaseSession(): CaseSessionContextValue {
  const context = useContext(CaseSessionContext);

  if (!context) {
    throw new Error("useCaseSession must be used within CaseSessionProvider.");
  }

  return context;
}